import FeatureGates, {
  type GetExperimentValueOptions,
} from '@atlaskit/feature-gate-js-client';

import {
  type FeatureFlagOverrides,
  isFeatureFlagEnabled,
} from '@canvas/client-feature-flags-state';
import {
  experimentConfig,
  experimentOverrides,
  isExperimentName,
  type ExperimentName,
  type ExperimentParams,
} from '@canvas/client-feature-flags-state/experiments';
import { captureException } from '@canvas/sentry-react';

export {
  experimentConfig,
  experimentOverrides,
} from '@canvas/client-feature-flags-state/experiments';

export type ExperimentVariant<TVariants extends string> = TVariants;

export const resolveExperimentVariant = <TVariant extends string>({
  value,
  variants,
  fallback,
}: {
  value: unknown;
  variants: readonly TVariant[];
  fallback: TVariant;
}): TVariant => {
  for (const variant of variants) {
    if (value === variant) {
      return variant;
    }
  }

  return fallback;
};

/**
 * Apply feature flag overrides to experiment values
 * This should be called during app initialization
 */
export const applyFeatureFlagOverridesToExperiments = (
  featureFlagOverrides: FeatureFlagOverrides,
) => {
  // Reset any existing overrides
  Object.keys(experimentOverrides).forEach((key) => {
    delete experimentOverrides[key as ExperimentName];
  });

  Object.entries(featureFlagOverrides).forEach(([flagName, value]) => {
    if (isExperimentName(flagName)) {
      experimentOverrides[flagName] = {
        ...experimentOverrides[flagName],
        enabled: value,
        isEnabled: value,
      };
    }
  });
};

/**
 * Apply experiment parameter overrides from URL query parameters
 * This allows setting specific experiment parameters via ?ep=experimentName.paramName.value
 * This should be called during app initialization
 */
export const applyExperimentParamOverrides = (
  paramOverrides: Record<string, Record<string, any>>,
) => {
  Object.entries(paramOverrides).forEach(([experimentName, params]) => {
    if (!isExperimentName(experimentName)) {
      console.warn(`Unknown experiment name in param overrides: "${experimentName}"`);
      return;
    }

    const config = experimentConfig[experimentName as ExperimentName];

    Object.entries(params).forEach(([paramName, value]) => {
      if (!(paramName in config.defaultParams)) {
        console.warn(
          `Unknown parameter "${paramName}" for experiment "${experimentName}"`,
        );
        return;
      }

      experimentOverrides[experimentName as ExperimentName] = {
        ...experimentOverrides[experimentName as ExperimentName],
        [paramName]: value,
      };
    });
  });
};

/**
 * Defining our own type so we can mark `fireExperimentExposure` as required to
 * enforce explicit opt-in of firing exposure events.
 */
type ExperimentValueOptions<T> = {
  /**
   * Whether to fire an experiment exposure event.
   *
   * NOTE - do *not* set this to `true` in module definitions/initialisation, as this will
   * make the exposure fire upon app load. Instead, shift the reading of the experiment
   * further into the product lifecycle, e.g. at the _absolute latest point in time_,
   * where the experience diverges - typically from a UI event occurring, e.g. user opens
   * a menu, or invokes an action that has an old/new - fire at time of invoking.
   *
   * Instead, two options:
   *
   * Option 1: Separate parameter (e.g. moduleEnabled), which gets set to true in control
   * Option 2: Separate gate.
   *
   * See {@link https://hello.atlassian.net/wiki/spaces/ExpWiki/pages/3187295823/Exposure+Events+101}
   */
  fireExperimentExposure: boolean;
} & GetExperimentValueOptions<T>;

/**
 * Returns the value of a given parameter for the provided experiment
 *
 * **NOTE**: You must explicitly set whether you want this call to fire an
 * exposure event or not. This is done with the `options` param (see {@link ExperimentValueOptions}).
 *
 * If you're unsure whether you should be firing an exposure event, reach out to
 * the team experimentation champions.
 *
 * This page explains the importance of exposure events: {@link https://hello.atlassian.net/wiki/spaces/~732385844/pages/3187295823/Exposure+Events+101}
 * Also see {@file docs/guides/how-to-use-experiment-exposure-events.md}
 *
 * @example getExperimentValue('whiteboardAATest', 'enabled', { fireExperimentExposure: true })
 * @example getExperimentValue('anotherExperiment', 'btnColor', { fireExperimentExposure: false })
 */
export const getExperimentValue = <
  const ExpName extends ExperimentName,
  const ParamName extends keyof ExperimentParams[ExpName],
>(
  experimentName: ExpName,
  paramName: ParamName,
  /**
   * Statsig options for fetching the experiment value. `fireExposureEvent` is required
   */
  options: ExperimentValueOptions<ExperimentParams[ExpName][ParamName]>,
  shouldSkipPrerequisiteFeatureFlags = false,
  shouldSkipPrerequisiteExperiments = false,
): ExperimentParams[ExpName][ParamName] => {
  // Check if there's an override for this experiment and parameter
  if (
    experimentOverrides[experimentName] &&
    paramName in experimentOverrides[experimentName]
  ) {
    const override = experimentOverrides[experimentName];
    return override[
      paramName as keyof typeof override
    ] as ExperimentParams[ExpName][ParamName];
  }

  const {
    experimentId,
    defaultParams,
    prerequisiteFeatureFlags,
    prerequisiteExperiments,
  } = experimentConfig[experimentName];

  // Typescript is unable to discriminate between experiment params
  // This should be okay to cast as TypeScript will throw an error if we try to call this
  // function with a param name that doesn't exist for the provided experiment
  const defaultParam = (defaultParams as ExperimentParams[ExpName])[paramName];

  if (prerequisiteFeatureFlags && !shouldSkipPrerequisiteFeatureFlags) {
    const prerequisiteFeatureFlagsResults =
      prerequisiteFeatureFlags.every(isFeatureFlagEnabled);
    if (!prerequisiteFeatureFlagsResults) {
      return defaultParam;
    }
  }

  if (prerequisiteExperiments && !shouldSkipPrerequisiteExperiments) {
    const prerequisiteExperimentsResults = prerequisiteExperiments.every(
      ({ experimentName, paramName, expectedValue }) =>
        getExperimentValue(
          experimentName as ExperimentName,
          paramName as never,
          { fireExperimentExposure: false },
          false,
          true,
        ) === expectedValue,
    );

    if (!prerequisiteExperimentsResults) {
      return defaultParam;
    }
  }

  return FeatureGates.getExperimentValue(
    experimentId,
    paramName as string,
    defaultParam,
    options,
  );
};

/**
 * Tells Statsig to manually log an exposure event, useful when you've already
 * evaluated an experiment but explicitly told it not to fire an experiment exposure
 *
 * Note that this is idempotent
 *
 * See: https://docs.statsig.com/client/jsClientSDK#manual-exposures-
 */
export const manuallyFireExposureEvent = (experimentName: ExperimentName) => {
  if (process.env.MODE === 'offline' || process.env.MODE === 'test') {
    return;
  }

  const experimentId = experimentConfig[experimentName].experimentId;

  if (!FeatureGates.initializeCompleted()) {
    captureException(
      new Error('Statsig manual exposure skipped before client initialization'),
      {
        tags: {
          captureLocation: 'manuallyFireExposureEvent-client-not-initialized',
        },
      },
    );
    return;
  }

  return FeatureGates.manuallyLogExperimentExposure(experimentId);
};

/**
 * Gets all experiment values without firing exposure events
 * This is intended for debugging surfaces only
 */
export const getAllExperimentValues = () => {
  const result: Record<string, Record<string, any>> = {};

  Object.entries(experimentConfig).forEach(([experimentName, config]) => {
    result[experimentName] = {};

    // For each parameter in the experiment, get its value
    Object.keys(config.defaultParams).forEach((paramName) => {
      const value = getExperimentValue(
        experimentName as ExperimentName,
        paramName as never,
        { fireExperimentExposure: false },
      );

      result[experimentName][paramName] = value;
    });
  });

  return result;
};

/**
 * Native Database milestone feature utilities
 *
 * These utilities check individual milestone feature flags to determine if a
 * specific feature should be enabled.
 */

/**
 * Check if the Visual Refresh milestone features should be enabled.
 * The first milestone is permanently enabled.
 */
export const isNativeDbVisualRefreshEnabled = (): boolean => {
  return true;
};

/**
 * M1 - Visual Refresh: Database Embed Visual Update
 * Covers:
 * - Framing around the database element removed
 * - Improved drag functionality for database elements
 *
 * Check if the Toolbar/Action Bar milestone features should be enabled.
 * @returns true if the toolbar/action bar flag is enabled
 */
export const isNativeDbToolbarActionBarEnabled = (): boolean => {
  const toolbarActionBarEnabled = isFeatureFlagEnabled('wbDbToolbarActionBar');

  return toolbarActionBarEnabled;
};

/**
 * Check if the Flags/Snackbars/Modals milestone features should be enabled.
 * @returns true if the flags/snackbars/modals flag is enabled
 */
export const isNativeDbFlagsSnackbarsModalsEnabled = (): boolean => {
  const flagsSnackbarsModalsEnabled = isFeatureFlagEnabled('wbDbFlagsSnackbarsModals');

  return flagsSnackbarsModalsEnabled;
};

/**
 * Check if the Hello Release Other milestone features should be enabled.
 * @returns true if the hello release other flag is enabled
 */
export const isNativeDbHelloReleaseOtherEnabled = (): boolean => {
  const helloReleaseOtherEnabled = isFeatureFlagEnabled('wbDbHelloReleaseOther');

  return helloReleaseOtherEnabled;
};

/**
 * Check if the Changeboarding milestone features should be enabled.
 * @returns true if the changeboarding flag is enabled
 */
export const isNativeDbChangeboardingEnabled = (): boolean => {
  const changeboardingEnabled = isFeatureFlagEnabled('wbDbChangeboarding');

  return changeboardingEnabled;
};

export const isPermissionsRbacDependenciesCallsitesEnabled = (): boolean => {
  return getExperimentValue('permissionsRbacDependenciesCallsites', 'isEnabled', {
    fireExperimentExposure: true,
  });
};
